import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './App.css';
import Header from './components/Header';
import Footer from './components/Footer';
import MainScreen from './components/MainScreen';
import GameScreen from './components/GameScreen';
import ScoresScreen from './components/ScoresScreen';
import UserConfigScreen from './components/UserConfigScreen';
import PolicyScreen from './components/PolicyScreen';
import AdminScreen from './components/AdminScreen';
import CookieConsent from './components/CookieConsent';
import Advertisement from './components/Advertisement';
import { fetchCurrentUser, fetchRandomBackgroundImage, clearApiCache } from './services/apiService';
import { trackPageView } from './utils/matomoTracker';

function App() {
  const { t } = useTranslation();
  const [currentScreen, setCurrentScreen] = useState('main'); // 'main', 'game', 'scores', 'config', 'policy', 'admin'
  const [user, setUser] = useState(null);
  const [backgroundImage, setBackgroundImage] = useState('');
  const [policyContent, setPolicyContent] = useState({
    title: '',
    content: ''
  });
  const [globalMuted, setGlobalMuted] = useState(false);
  const [globalVolume, setGlobalVolume] = useState(100);
  const [gameState, setGameState] = useState({
    currentRound: 0,
    scores: [],
    difficulty: 'medium',
    gamemode: null,
    sessionId: null
  });

  // Check if user is logged in on component mount
  useEffect(() => {
    const checkUser = async () => {
      try {
        const userData = await fetchCurrentUser();
        if (userData && !userData.error) {
          setUser(userData);
        }
      } catch (error) {
        console.error('Error fetching user:', error);
      }
    };

    checkUser();
  }, []);

  // Set random background image on component mount
  useEffect(() => {
    const loadBackgroundImage = async () => {
      try {
        const imagePath = await fetchRandomBackgroundImage();
        setBackgroundImage(imagePath);
      } catch (error) {
        console.error('Error loading background image:', error);
        setBackgroundImage(''); // Set empty string if there's an error
      }
    };

    loadBackgroundImage();
  }, []);

  // Track page views when screen changes
  useEffect(() => {
    // Map screen names to more readable page names for analytics
    const pageNames = {
      main: 'Home',
      game: 'Game',
      scores: 'Leaderboard',
      config: 'User Settings',
      policy: 'Policy',
      admin: 'Admin Panel'
    };

    // Track the page view with the appropriate name
    trackPageView(pageNames[currentScreen] || currentScreen);
  }, [currentScreen]);

  // Function to start a new game
  const startGame = (difficulty, gamemode) => {
    // Clear API cache to ensure fresh data when starting a new game
    clearApiCache();

    setGameState(prevState => ({
      ...prevState,
      currentRound: 1,
      scores: [],
      difficulty: difficulty || 'medium',
      gamemode: gamemode, // Add gamemode to the game state
      sessionId: null,
      challengeId: null
    }));
    setCurrentScreen('game');
  };

  // Function to show scores screen
  const showScores = () => {
    setCurrentScreen('scores');
  };

  // Function to return to main screen
  const returnToMainScreen = () => {
    // Clear API cache to ensure fresh data when returning to main screen
    clearApiCache();

    // Reset game state when returning to main screen
    setGameState(prevState => ({
      ...prevState,
      currentRound: 0,
      scores: [],
      sessionId: null,
      challengeId: null
    }));
    setCurrentScreen('main');
  };

  // Function to show user configuration screen
  const showUserConfig = () => {
    setCurrentScreen('config');
  };

  // Function to show admin screen
  const showAdmin = () => {
    setCurrentScreen('admin');
  };

  // Function to show policy screen
  const showPolicy = (type) => {
    let title = '';
    let content = '';

    switch (type) {
      case 'cookie':
        title = t('policies.cookieTitle');
        content = t('policies.cookieContent');
        break;
      case 'privacy':
        title = t('policies.privacyTitle');
        content = t('policies.privacyContent');
        break;
      case 'terms':
        title = t('policies.termsTitle');
        content = t('policies.termsContent');
        break;
      default:
        break;
    }

    setPolicyContent({ title, content });
    setCurrentScreen('policy');
  };

  // Function to update game state
  const updateGameState = (newState) => {
    setGameState(prevState => ({
      ...prevState,
      ...newState
    }));
  };

  return (
    <div 
      className="App"
      style={{
        ...(backgroundImage && backgroundImage.startsWith('#') 
          ? { backgroundColor: backgroundImage } // Use as background color
          : { 
              backgroundImage: backgroundImage ? `url(${backgroundImage})` : 'none',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              backgroundAttachment: 'fixed'
            }
        )
      }}
    >
      <Header 
        user={user} 
        onShowScores={showScores} 
        onReturnToMain={returnToMainScreen}
        onShowUserConfig={showUserConfig}
        onShowAdmin={showAdmin}
      />

      {currentScreen === 'main' && (
        <MainScreen 
          onStartGame={startGame} 
          user={user}
        />
      )}

      {currentScreen === 'game' && (
        <GameScreen 
          gameState={gameState}
          updateGameState={updateGameState}
          onGameEnd={returnToMainScreen}
          user={user}
          globalMuted={globalMuted}
          setGlobalMuted={setGlobalMuted}
          globalVolume={globalVolume}
          setGlobalVolume={setGlobalVolume}
        />
      )}

      {currentScreen === 'scores' && (
        <ScoresScreen 
          user={user}
          onClose={returnToMainScreen}
        />
      )}

      {currentScreen === 'config' && (
        <UserConfigScreen 
          user={user}
          onClose={returnToMainScreen}
          onUserUpdate={setUser}
          globalMuted={globalMuted}
          globalVolume={globalVolume}
          setGlobalVolume={setGlobalVolume}
        />
      )}

      {currentScreen === 'policy' && (
        <PolicyScreen 
          title={policyContent.title}
          content={policyContent.content}
          onClose={returnToMainScreen}
        />
      )}

      {currentScreen === 'admin' && (
        <AdminScreen 
          user={user}
        />
      )}
      <CookieConsent />
      <Advertisement user={user} />
      <Footer onShowPolicy={showPolicy} />
    </div>
  );
}

export default App;
